/**
 * Single sign-on through the hospital's own identity provider.
 *
 * Email-first: the address decides. If its domain belongs to an organization
 * with SSO switched on, the person is sent to that provider (Microsoft Entra
 * ID for most hospitals) and comes back to /sso/complete with a one-time
 * code, which is exchanged for a session here. Otherwise the emailed link
 * remains the door.
 */

import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { call } from '../lib/callable';

/**
 * SINGLE SIGN-ON IS WRITTEN AND NOT RELEASED (owner, 2026-09-23).
 *
 * It has never carried a sign-in: no run against a mock issuer, none against a
 * real tenant, zero `sso_sessions` in production, no organization with
 * `sso.enabled`. So the customer app offers no way to set it up, and this one
 * constant says why rather than the entry quietly disappearing.
 *
 * Nothing below is deleted or disabled: `resolveEmailOrg`, `needsSso`,
 * `offersSso` and `initiateSsoLogin` all still work, and would come to life
 * the moment an organization had it switched on from the console. Only the
 * CONFIGURATION panel is withheld. Its backend twin is
 * `functions/src/sso/release.ts`, which refuses a customer's System Admin at
 * the callable, so this is presentation and not the enforcement.
 */
export const SSO_RELEASED_TO_CUSTOMERS: boolean = false;

export interface EmailOrg {
  found: boolean;
  orgId?: string;
  orgName?: string;
  authMode?: 'password_only' | 'mixed' | 'sso_only' | 'social_only';
  ssoEnabled?: boolean;
  ssoProvider?: 'saml' | 'oidc' | null;
}

/** Which organization an address belongs to, and whether it signs in by SSO. */
export function resolveEmailOrg(email: string): Promise<EmailOrg> {
  return call<{ email: string }, EmailOrg>('resolveEmailOrg', { email });
}

/**
 * Whether this address must be sent to an identity provider: single sign-on
 * is on AND required. In mixed mode it is offered, not imposed — see
 * offersSso — so a contractor without an Entra account, or anyone while
 * the hospital is still trying it out, keeps the emailed link.
 */
export function needsSso(org: EmailOrg | null): org is EmailOrg & { orgId: string } {
  return Boolean(org?.found && org.ssoEnabled && org.orgId && org.authMode === 'sso_only');
}

/** Whether the person may choose the provider, with the emailed link still open. */
export function offersSso(org: EmailOrg | null): org is EmailOrg & { orgId: string } {
  return Boolean(org?.found && org.ssoEnabled && org.orgId && org.authMode !== 'sso_only');
}

/** Start the provider's sign-in; the caller sends the browser to redirectUrl. */
export function initiateSsoLogin(orgId: string, email: string): Promise<{ sessionId: string; redirectUrl: string; provider: string }> {
  return call<{ orgId: string; email: string }, { sessionId: string; redirectUrl: string; provider: string }>('initiateSsoLogin', { orgId, email });
}

export interface SsoExchange {
  customToken: string;
  uid: string;
  email: string;
  isNewUser: boolean;
  roles: string[];
  orgId: string;
  orgName: string;
}

/** Exchange the one-time code from the callback for a session. */
export async function completeSsoSignIn(code: string): Promise<SsoExchange> {
  const result = await call<{ code: string }, SsoExchange>('exchangeSsoCode', { code });
  await signInWithCustomToken(auth, result.customToken);
  return result;
}

/** What an error from the provider means, in words. */
export function describeSsoError(error: string | null, description: string | null): string {
  switch (error) {
    case 'access_denied': return 'Your organization\'s sign-in refused the request. Ask your IT team whether your account is allowed to use StandingDocs.';
    case 'invalid_state': case 'session_not_found': case 'session_expired': return 'The sign-in took too long or was started twice. Try again from the sign-in page.';
    case 'missing_email': return 'Your organization\'s sign-in did not send an email address, so we cannot tell who you are. Ask your IT team to include the email claim.';
    case 'config_error': return 'Single sign-on is not fully set up for your organization yet.';
    case 'domain_not_allowed': return 'Your Microsoft account\'s address is not one of this organization\'s email domains, so it cannot sign in here. Guest accounts cannot be used; ask your IT team.';
    case 'token_error': case 'validation_failed': return 'Your organization\'s sign-in could not be verified. Try again; if it keeps happening, tell your IT team.';
    default: return description || 'Single sign-on did not complete.';
  }
}
